import { getAnalyticsInstance } from '@/lib/firebase';
import { logEvent, setUserId, setUserProperties } from 'firebase/analytics';

/**
 * Check if analytics is enabled and available
 * @returns {boolean} Whether analytics is available
 */
const isAnalyticsAvailable = () => {
  const analytics = getAnalyticsInstance();
  return analytics !== null;
};

/**
 * Safely log analytics events with error handling
 * @param {Function} analyticsFunction - Firebase analytics function to execute
 * @param {string} eventName - Name of the event for error logging
 * @returns {boolean} Whether the event was logged successfully
 */
const safeAnalyticsCall = (analyticsFunction, eventName) => {
  try {
    analyticsFunction();
    return true;
  } catch (error) {
    if (import.meta.env.MODE === 'development') {
      console.error(`Error tracking ${eventName}:`, error);
    }
    return false;
  }
};

/**
 * Log a custom event to Firebase Analytics
 * @param {string} eventName - Name of the event (max 40 characters, alphanumeric and underscores only)
 * @param {Object} [eventParams={}] - Event parameters (max 25 parameters)
 * @returns {boolean} Whether the event was tracked successfully
 * @example
 * trackEvent('add_to_cart', {
 *   item_id: 'SKU123',
 *   item_name: 'Blue T-Shirt',
 *   price: 19.99
 * });
 */
export const trackEvent = (eventName, eventParams = {}) => {
  if (!isAnalyticsAvailable()) {
    if (import.meta.env.MODE === 'development') {
      console.warn('Analytics not available. Event not tracked:', eventName);
    }
    return false;
  }

  // Validate event name (Firebase requirement)
  if (!eventName || eventName.length > 40) {
    console.error('Invalid event name:', eventName);
    return false;
  }

  const analytics = getAnalyticsInstance();

  return safeAnalyticsCall(() => {
    logEvent(analytics, eventName, eventParams);
  }, eventName);
};

/**
 * Track page view
 * @param {string} pagePath - Page path (e.g., '/products/123')
 * @param {string} [pageTitle] - Page title (defaults to document.title)
 * @returns {boolean} Whether the event was tracked successfully
 * @example
 * trackPageView('/products/123', 'Product Details - Blue T-Shirt');
 */
export const trackPageView = (pagePath, pageTitle) => {
  return trackEvent('page_view', {
    page_path: pagePath,
    page_title: pageTitle || document.title,
    page_location: window.location.href,
  });
};

/**
 * Track user login
 * @param {string} method - Login method (e.g., 'google', 'email', 'facebook')
 * @returns {boolean} Whether the event was tracked successfully
 * @example
 * trackLogin('google');
 */
export const trackLogin = (method) => {
  return trackEvent('login', { method });
};

/**
 * Track user signup
 * @param {string} method - Signup method (e.g., 'google', 'email', 'facebook')
 * @returns {boolean} Whether the event was tracked successfully
 * @example
 * trackSignup('email');
 */
export const trackSignup = (method) => {
  return trackEvent('sign_up', { method });
};

/**
 * Track search
 * @param {string} searchTerm - Search query entered by user
 * @returns {boolean} Whether the event was tracked successfully
 * @example
 * trackSearch('safety equipment');
 */
export const trackSearch = (searchTerm) => {
  return trackEvent('search', { search_term: searchTerm });
};

/**
 * Track item view (e.g., product view)
 * @param {Object} item - Item details
 * @param {string} item.id - Item ID or SKU
 * @param {string} item.name - Item name
 * @param {string} [item.category] - Item category
 * @param {number} [item.price] - Item price
 * @returns {boolean} Whether the event was tracked successfully
 * @example
 * trackItemView({
 *   id: 'SKU123',
 *   name: 'Blue T-Shirt',
 *   category: 'Apparel',
 *   price: 19.99
 * });
 */
export const trackItemView = (item) => {
  if (!item?.id || !item?.name) {
    console.error('Invalid item data for tracking');
    return false;
  }

  return trackEvent('view_item', {
    items: [
      {
        item_id: item.id,
        item_name: item.name,
        item_category: item.category,
        price: item.price,
      },
    ],
  });
};

/**
 * Track add to cart
 * @param {Object} item - Item details
 * @param {string} item.id - Item ID or SKU
 * @param {string} item.name - Item name
 * @param {number} item.quantity - Quantity added to cart
 * @param {number} [item.price] - Item price per unit
 * @param {string} [item.category] - Item category
 * @returns {boolean} Whether the event was tracked successfully
 * @example
 * trackAddToCart({
 *   id: 'SKU123',
 *   name: 'Blue T-Shirt',
 *   quantity: 2,
 *   price: 19.99,
 *   category: 'Apparel'
 * });
 */
export const trackAddToCart = (item) => {
  if (!item?.id || !item?.name || !item?.quantity) {
    console.error('Invalid item data for add to cart tracking');
    return false;
  }

  return trackEvent('add_to_cart', {
    items: [
      {
        item_id: item.id,
        item_name: item.name,
        quantity: item.quantity,
        price: item.price,
        item_category: item.category,
      },
    ],
    value: item.price ? item.price * item.quantity : undefined,
    currency: 'USD',
  });
};

/**
 * Track remove from cart
 * @param {Object} item - Item details
 * @param {string} item.id - Item ID or SKU
 * @param {string} item.name - Item name
 * @param {number} item.quantity - Quantity removed from cart
 * @param {number} [item.price] - Item price per unit
 * @param {string} [item.category] - Item category
 * @returns {boolean} Whether the event was tracked successfully
 * @example
 * trackRemoveFromCart({
 *   id: 'SKU123',
 *   name: 'Blue T-Shirt',
 *   quantity: 1,
 *   price: 19.99
 * });
 */
export const trackRemoveFromCart = (item) => {
  if (!item?.id || !item?.name || !item?.quantity) {
    console.error('Invalid item data for remove from cart tracking');
    return false;
  }

  return trackEvent('remove_from_cart', {
    items: [
      {
        item_id: item.id,
        item_name: item.name,
        quantity: item.quantity,
        price: item.price,
        item_category: item.category,
      },
    ],
    value: item.price ? item.price * item.quantity : undefined,
    currency: 'USD',
  });
};

/**
 * Track begin checkout
 * @param {Object} cart - Cart details
 * @param {Array<Object>} cart.items - Items in cart
 * @param {number} cart.value - Total cart value
 * @param {string} [cart.currency='USD'] - Currency code
 * @returns {boolean} Whether the event was tracked successfully
 * @example
 * trackBeginCheckout({
 *   items: [{ item_id: 'SKU123', item_name: 'Blue T-Shirt', quantity: 2, price: 19.99 }],
 *   value: 39.98,
 *   currency: 'USD'
 * });
 */
export const trackBeginCheckout = (cart) => {
  if (!cart?.items || !Array.isArray(cart.items) || cart.items.length === 0) {
    console.error('Invalid cart data for begin checkout tracking');
    return false;
  }

  return trackEvent('begin_checkout', {
    items: cart.items,
    value: cart.value,
    currency: cart.currency || 'USD',
  });
};

/**
 * Track purchase/transaction
 * @param {Object} transaction - Transaction details
 * @param {string} transaction.id - Transaction/Order ID
 * @param {number} transaction.value - Transaction total value
 * @param {string} [transaction.currency='USD'] - Currency code (ISO 4217)
 * @param {number} [transaction.tax] - Tax amount
 * @param {number} [transaction.shipping] - Shipping cost
 * @param {Array<Object>} transaction.items - Items purchased
 * @returns {boolean} Whether the event was tracked successfully
 * @example
 * trackPurchase({
 *   id: 'ORD-123',
 *   value: 49.98,
 *   currency: 'USD',
 *   tax: 4.50,
 *   shipping: 5.00,
 *   items: [
 *     { item_id: 'SKU123', item_name: 'Blue T-Shirt', quantity: 2, price: 19.99 }
 *   ]
 * });
 */
export const trackPurchase = (transaction) => {
  if (!transaction?.id || !transaction?.value || !transaction?.items) {
    console.error('Invalid transaction data for purchase tracking');
    return false;
  }

  return trackEvent('purchase', {
    transaction_id: transaction.id,
    value: transaction.value,
    currency: transaction.currency || 'USD',
    tax: transaction.tax,
    shipping: transaction.shipping,
    items: transaction.items,
  });
};

/**
 * Track refund
 * @param {Object} refund - Refund details
 * @param {string} refund.id - Transaction/Order ID that was refunded
 * @param {number} refund.value - Refund amount
 * @param {string} [refund.currency='USD'] - Currency code
 * @returns {boolean} Whether the event was tracked successfully
 * @example
 * trackRefund({
 *   id: 'ORD-123',
 *   value: 49.98,
 *   currency: 'USD'
 * });
 */
export const trackRefund = (refund) => {
  if (!refund?.id || !refund?.value) {
    console.error('Invalid refund data for tracking');
    return false;
  }

  return trackEvent('refund', {
    transaction_id: refund.id,
    value: refund.value,
    currency: refund.currency || 'USD',
  });
};

/**
 * Set user ID for analytics
 * Call this when user logs in
 * @param {string} userId - User ID (must be unique and consistent)
 * @returns {boolean} Whether the user ID was set successfully
 * @example
 * setAnalyticsUserId('user-12345');
 */
export const setAnalyticsUserId = (userId) => {
  if (!isAnalyticsAvailable()) {
    return false;
  }

  if (!userId || typeof userId !== 'string') {
    console.error('Invalid user ID for analytics');
    return false;
  }

  const analytics = getAnalyticsInstance();

  return safeAnalyticsCall(() => {
    setUserId(analytics, userId);
  }, 'setUserId');
};

/**
 * Set user properties for analytics
 * User properties are attributes you define to describe segments of your user base
 * @param {Object} properties - User properties (max 25 properties)
 * @returns {boolean} Whether the user properties were set successfully
 * @example
 * setAnalyticsUserProperties({
 *   account_type: 'premium',
 *   preferred_language: 'en',
 *   registration_date: '2024-01-01'
 * });
 */
export const setAnalyticsUserProperties = (properties) => {
  if (!isAnalyticsAvailable()) {
    return false;
  }

  if (
    !properties ||
    typeof properties !== 'object' ||
    Object.keys(properties).length === 0
  ) {
    console.error('Invalid properties for analytics');
    return false;
  }

  // Validate that we don't exceed 25 properties
  if (Object.keys(properties).length > 25) {
    console.error('Too many user properties (max 25)');
    return false;
  }

  const analytics = getAnalyticsInstance();

  return safeAnalyticsCall(() => {
    setUserProperties(analytics, properties);
  }, 'setUserProperties');
};

/**
 * Track custom conversion
 * Use this for tracking custom business-specific conversion events
 * @param {string} conversionName - Conversion event name
 * @param {Object} [conversionData={}] - Conversion data
 * @returns {boolean} Whether the event was tracked successfully
 * @example
 * trackConversion('lead_generated', {
 *   lead_source: 'contact_form',
 *   lead_value: 100
 * });
 */
export const trackConversion = (conversionName, conversionData = {}) => {
  if (!conversionName || typeof conversionName !== 'string') {
    console.error('Invalid conversion name');
    return false;
  }

  return trackEvent(conversionName, conversionData);
};
